import React, { useEffect, useState } from "react";

import { CheckOutlined } from "@ant-design/icons";
import { Card, Form, Input, Button } from "antd";
import Parse from "parse";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import OtpCodeInput, { CODE_LENGTH, emptyCode } from "./OtpCodeInput.jsx";
import logoDark from "../assets/nomyx_logo_dark.png";

// Parse reports a dropped connection as CONNECTION_FAILED (100) with the SDK's own wording
// ("XMLHttpRequest failed: …"), which isn't meant for users. Other messages — such as the rate
// limiter's "Too many requests…" — pass through.
const connectionErrorMessage = (error) =>
  String(error?.message ?? "").startsWith("XMLHttpRequest failed") ? "We couldn't reach the server. Please check your connection and try again." : error?.message;

// Email, then the emailed code, then the new password — one card, swapped in place.
// mode "forgot" (/forgot-password) asks for a code at the email step. mode "create"
// (/create-password, the link in a new admin's setup email) already has one, so the email
// step only says which address it went to.
const TEXT = {
  forgot: {
    titles: { email: "Forgot Password", verify: "Enter Verification Code", reset: "Reset Password" },
    intro: "Enter your email, and we will send you a code to reset your password.",
    done: "Password reset successfully! Please log in with your new password.",
  },
  create: {
    titles: { email: "Create Password", verify: "Enter Setup Code", reset: "Set Your Password" },
    intro: "Enter the email address your password setup code was sent to.",
    done: "Password set up successfully! Please log in.",
  },
};

const ForgotPasswordPage = ({ mode = "forgot" }) => {
  const text = TEXT[mode];
  const [step, setStep] = useState("email"); // "email" | "verify" | "reset"
  const [email, setEmail] = useState("");
  const [code, setCode] = useState(emptyCode());
  const [codeError, setCodeError] = useState("");
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [resending, setResending] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [password, setPassword] = useState("");
  const navigate = useNavigate();

  // A stale/revoked Parse session (left behind by logout, which doesn't clear
  // the Parse SDK's own local cache)
  useEffect(() => {
    if (Parse.User.current()) {
      Parse.User.logOut().catch(() => {});
    }
  }, []);

  // The password policy: what the checklist shows and what submitting checks.
  const criteriaFor = (value) => ({
    minLength: value.length >= 8,
    hasUppercase: /[A-Z]/.test(value),
    hasLowercase: /[a-z]/.test(value),
    hasNumber: /\d/.test(value),
    hasSpecialChar: /[!@#$%^&*(),.?":{}|<>]/.test(value),
  });
  const passwordCriteria = criteriaFor(password);

  const renderIcon = (condition) =>
    condition ? <CheckOutlined className="!text-green-500" /> : <span className="w-2 h-2 rounded-full bg-gray-500 inline-block" />;

  // Resolves with the same neutral message for every email, or rejects (e.g. rate limited).
  // For an admin who hasn't set a first password, the backend sends the setup email again.
  const requestCode = async (emailValue) => {
    const response = await Parse.Cloud.run("requestPasswordReset", { email: emailValue });
    toast.success(response.message);
  };

  // To the code step (or back to the email step) with nothing left from this attempt. The password
  // form unmounts on the way, so its checklist starts empty again too.
  const restart = (to) => {
    setCode(emptyCode());
    setCodeError("");
    setPassword("");
    setStep(to);
  };

  // Step 1: email
  const handleEmailSubmit = async (values) => {
    const emailValue = values.email.toLowerCase().trim();
    if (mode === "create") {
      setEmail(emailValue);
      restart("verify");
      return;
    }
    setLoading(true);
    try {
      await requestCode(emailValue);
      setEmail(emailValue);
      restart("verify");
    } catch (error) {
      toast.error(connectionErrorMessage(error) || "Failed to send password reset email!");
    } finally {
      setLoading(false);
    }
  };

  // Step 2: code
  const handleVerify = async () => {
    const fullCode = code.join("");
    if (fullCode.length < CODE_LENGTH) {
      setCodeError(`Please enter the complete ${CODE_LENGTH}-digit code.`);
      return;
    }
    if (verifying) return;
    setVerifying(true);
    try {
      await Parse.Cloud.run("verifyPasswordResetCode", { email, code: fullCode });
      setStep("reset");
    } catch (error) {
      // Parse's CONNECTION_FAILED (100) is the rate limiter ("Too many requests…") or the network.
      setCodeError(error?.code === 100 ? connectionErrorMessage(error) : "Invalid or expired code. Please try again or request a new one.");
    } finally {
      setVerifying(false);
    }
  };

  const handleResend = async () => {
    setResending(true);
    setCode(emptyCode());
    setCodeError("");
    try {
      await requestCode(email);
    } catch (error) {
      toast.error(connectionErrorMessage(error) || "We couldn't send a new code. Please try again.");
    } finally {
      setResending(false);
    }
  };

  // Step 3: new password
  const handleResetSubmit = async (values) => {
    if (!Object.values(criteriaFor(values.password)).every(Boolean)) {
      toast.error("Please meet all password requirements.");
      return;
    }
    setResetting(true);
    try {
      await Parse.Cloud.run("resetPassword", { email, code: code.join(""), newPassword: values.password });
      toast.success(text.done);
      navigate("/login");
    } catch (error) {
      console.error(error);
      if (error?.code === 100) {
        // Rate limited or offline: the code may still be good, so keep the typed password.
        toast.error(connectionErrorMessage(error) || "We couldn't reach the server. Please try again.");
      } else {
        // The code expired (or was replaced) since it was checked: get a fresh one.
        toast.error("That code is no longer valid. Please request a new one.");
        restart("verify");
      }
    } finally {
      setResetting(false);
    }
  };

  return (
    <div
      className="relative w-full min-h-screen overflow-hidden flex flex-col"
      style={{
        backgroundImage: "url('/images/nomyx_banner.svg')",
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
    >
      <h1 className="text-right font-bold text-xl mb-4 w-full mt-8 !-ml-10 text-white">NomyxID</h1>
      <div className="flex flex-1 flex-col lg:flex-row">
        {/* Left Side */}
        <div className="w-full lg:w-1/2 flex flex-col items-center justify-center px-4 md:px-6 my-10">
          <div className="w-full max-w-2xl">
            <img src={logoDark} alt="Logo" width={630} height={240} priority="true" />
          </div>
        </div>

        {/* Right Side */}
        <div className="max-[550px]:hidden w-1/2 flex flex-col justify-center items-center p-2">
          <div className="flex-grow flex items-center justify-center w-full">
            <div className="bg-nomyxDark1 bg-opacity-90 text-nomyxWhite shadow-lg rounded-lg p-4 max-w-2xl w-full">
              <div className="w-full flex flex-col justify-center items-center">
                <Card
                  title={<span className="text-white">{text.titles[step]}</span>}
                  style={{ width: "100%", maxWidth: "550px", border: "none" }}
                  className="signup-card bg-transparent shadow-lg rounded-lg"
                >
                  {/* Step 1: Email */}
                  {step === "email" && (
                    <>
                      <p className="mb-6 text-left !text-white font-normal text-sm">{text.intro}</p>
                      <Form layout="vertical" onFinish={handleEmailSubmit} initialValues={{ email }} className="w-full">
                        <Form.Item
                          name="email"
                          label={<span className="text-gray-400">Email</span>}
                          rules={[
                            { required: true, message: "Please input your email!" },
                            { type: "email", message: "Please enter a valid email address!" },
                          ]}
                        >
                          <Input
                            type="email"
                            placeholder="Please enter your email"
                            className="signup-input placeholder-nomyxGray1 !border-nomyxGray1"
                          />
                        </Form.Item>

                        <Form.Item>
                          <div className="flex justify-between items-center">
                            <Link to="/login" className="font-semibold text-blue-400">
                              Back to Login
                            </Link>
                            <Button type="primary" htmlType="submit" className="signup-button" loading={loading}>
                              {mode === "create" ? "Continue" : "Submit"}
                            </Button>
                          </div>
                        </Form.Item>
                      </Form>
                    </>
                  )}

                  {/* Step 2: Code */}
                  {step === "verify" && (
                    <>
                      <p className="text-gray-400 mb-6 text-sm">
                        If <b className="text-white">{email}</b> has an account, we&apos;ve sent it a {CODE_LENGTH}-digit code. Enter it below to
                        continue.
                      </p>

                      <OtpCodeInput
                        code={code}
                        onChange={(next) => {
                          setCode(next);
                          setCodeError("");
                        }}
                        onEnter={handleVerify}
                        boxClassName="w-12 h-14 text-center text-xl font-bold rounded-md border-2 border-gray-600 bg-transparent text-white focus:border-blue-500 focus:outline-none transition-colors"
                      />

                      {codeError && <p className="text-red-500 text-sm text-center mb-4">{codeError}</p>}

                      <Button
                        type="primary"
                        onClick={handleVerify}
                        loading={verifying}
                        disabled={code.join("").length < CODE_LENGTH}
                        className="otp-verify-btn w-full mb-4"
                      >
                        Verify
                      </Button>

                      <div className="flex items-center justify-between">
                        <Button type="link" onClick={() => restart("email")} className="text-gray-400 text-xs p-0">
                          Use a different email
                        </Button>
                        <div>
                          <span className="text-gray-400 text-sm">Didn&apos;t receive the code?&nbsp;</span>
                          <Button type="link" onClick={handleResend} loading={resending} className="text-blue-400 p-0 text-sm">
                            Resend Code
                          </Button>
                        </div>
                      </div>
                    </>
                  )}

                  {/* Step 3: New password */}
                  {step === "reset" && (
                    <Form layout="vertical" onFinish={handleResetSubmit} className="w-full">
                      <Form.Item
                        name="password"
                        label={<span className="text-gray-400">Password</span>}
                        rules={[{ required: true, message: "Please enter your password!" }]}
                      >
                        <Input.Password
                          placeholder="Password"
                          onChange={(e) => setPassword(e.target.value)}
                          className="signup-input placeholder-nomyxGray1 !border-nomyxGray1"
                        />
                      </Form.Item>

                      <Form.Item
                        name="confirmPassword"
                        label={<span className="text-gray-400">Confirm Password</span>}
                        dependencies={["password"]}
                        rules={[
                          { required: true, message: "Please confirm your password!" },
                          ({ getFieldValue }) => ({
                            validator(_, value) {
                              if (!value || getFieldValue("password") === value) return Promise.resolve();
                              return Promise.reject(new Error("Passwords do not match!"));
                            },
                          }),
                        ]}
                      >
                        <Input.Password placeholder="Confirm Password" className="signup-input placeholder-nomyxGray1 !border-nomyxGray1" />
                      </Form.Item>

                      <div className="text-sm">
                        {[
                          { label: "At least 8 characters", condition: passwordCriteria.minLength },
                          { label: "At least 1 uppercase letter", condition: passwordCriteria.hasUppercase },
                          { label: "At least 1 lowercase letter", condition: passwordCriteria.hasLowercase },
                          { label: "At least 1 number", condition: passwordCriteria.hasNumber },
                          { label: "At least 1 special character", condition: passwordCriteria.hasSpecialChar },
                        ].map(({ label, condition }) => (
                          <p key={label} className="flex items-center">
                            {renderIcon(condition)}
                            <span className={`ml-2 ${condition ? "text-green-500" : "text-gray-500"}`}>{label}</span>
                          </p>
                        ))}
                      </div>

                      <Form.Item>
                        <div className="flex justify-end">
                          <Button type="primary" htmlType="submit" className="signup-button" loading={resetting}>
                            Submit
                          </Button>
                        </div>
                      </Form.Item>
                    </Form>
                  )}
                </Card>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
